import React, { useMemo, useCallback } from 'react';
import type { ValidationStatus } from '../types';
import { useSound } from '../../sounds';
import type { VisualFractionJSON } from '../../../transformers/zod-schemas';
import {
  getTextColorForBackground,
  getGroupClassName,
  resolveDistinctGroupPalette,
} from '../../utils/group-palette';
import { sumCounts, countsToPartGroups } from './part-utils';
import { useVisualFractionState } from './use-visual-fraction-state';
import {
  VISUAL_FRACTION_DISPLAY_COLOR,
  VISUAL_FRACTION_BORDER_COLOR,
} from './types';
import type { VisualFractionPart } from './types';
import { VISUAL_FRACTION_SVG_SIZE_SCALE } from '../../utils/visual-fraction-geometry';
import { VISUAL_FRACTION_SHAPE } from '../../../ast/inline';
import type { VisualFractionShape } from '../../../ast/inline';
import { BarShape } from './shapes/BarShape';
import { GridShape } from './shapes/GridShape';
import { CircleShape } from './shapes/CircleShape';
import { TriangleShape } from './shapes/TriangleShape';

type ShapeComponentProps = {
  parts: VisualFractionPart[];
  divisions: number;
  orientation: 'horizontal' | 'vertical';
  onPartClick: (index: number) => void;
};

const ShapeBar: React.FC<ShapeComponentProps> = ({ parts, divisions, orientation, onPartClick }) => (
  <BarShape parts={parts} divisions={divisions} orientation={orientation} onPartClick={onPartClick} />
);
const ShapeCircle: React.FC<ShapeComponentProps> = ({ parts, divisions, onPartClick }) => (
  <CircleShape parts={parts} divisions={divisions} onPartClick={onPartClick} />
);
const ShapeTriangle: React.FC<ShapeComponentProps> = ({ parts, divisions, onPartClick }) => (
  <TriangleShape parts={parts} divisions={divisions} onPartClick={onPartClick} />
);
const ShapeGrid: React.FC<ShapeComponentProps> = ({ parts, divisions, onPartClick }) => (
  <GridShape parts={parts} divisions={divisions} onPartClick={onPartClick} />
);

const SHAPE_COMPONENTS: Record<VisualFractionShape, React.ComponentType<ShapeComponentProps>> = {
  [VISUAL_FRACTION_SHAPE.BAR]: ShapeBar,
  [VISUAL_FRACTION_SHAPE.CIRCLE]: ShapeCircle,
  [VISUAL_FRACTION_SHAPE.TRIANGLE]: ShapeTriangle,
  [VISUAL_FRACTION_SHAPE.GRID]: ShapeGrid,
};

export interface VisualFractionProps {
  widgetId: string;
  widget: VisualFractionJSON;
  value?: number[];
  validationStatus?: ValidationStatus;
  onChange: (widgetId: string, value: number[]) => void;
}

/**
 * VisualFraction widget
 *
 * Renders a shape divided into equal parts.
 * - `filled` parts are display-only (group colors from the filled palette).
 * - Non-filled parts are clickable when `answer` is non-empty; clicks cycle
 *   through answer groups then back to empty.
 *
 * Colors are exposed through stable CSS classes and custom properties so
 * consuming apps can override them without touching JS.
 */
export const VisualFraction: React.FC<VisualFractionProps> = ({
  widgetId,
  widget,
  value,
  validationStatus = null,
  onChange,
}) => {
  const { shape, divisions, filled, answer, orientation, label } = widget;

  const filledCount = sumCounts(filled);
  const filledPartGroups = useMemo(() => countsToPartGroups(filled), [filled]);

  // Palette for display-only (filled) parts — seed with the brand purple.
  const filledPalette = useMemo(() => {
    if (filled.length === 0) return [];
    return resolveDistinctGroupPalette(filled.length, {
      seedColors: [VISUAL_FRACTION_DISPLAY_COLOR],
      excludeColors: [VISUAL_FRACTION_BORDER_COLOR],
    });
  }, [filled]);

  // Palette for interactive (answer) parts — must not reuse filled colors.
  const answerPalette = useMemo(() => {
    if (answer.length === 0) return [];
    return resolveDistinctGroupPalette(answer.length, {
      excludeColors: [VISUAL_FRACTION_BORDER_COLOR, ...filledPalette],
    });
  }, [answer, filledPalette]);

  const { partAssignments, handlePartClick } = useVisualFractionState({
    widgetId,
    divisions,
    filledCount,
    answer,
    value,
    onChange,
  });

  // Sound only on a real shade toggle — interactive slots (answer set,
  // non-filled). Filled/display clicks no-op in handlePartClick, so stay silent.
  const playSound = useSound();
  const handlePartClickWithSound = useCallback(
    (index: number) => {
      if (answer.length > 0 && index >= filledCount) playSound('fraction-shade');
      handlePartClick(index);
    },
    [answer.length, filledCount, playSound, handlePartClick],
  );

  // Build part descriptors once; shape components stay stateless.
  const parts = useMemo<VisualFractionPart[]>(
    () =>
      Array.from({ length: divisions }, (_, i) => {
        let partClass = 'visual-fraction-widget__part visual-fraction-widget__part--empty';
        let groupIdx: number | null = null;

        if (i < filledCount) {
          groupIdx = filledPartGroups[i] ?? 0;
          partClass = 'visual-fraction-widget__part visual-fraction-widget__part--filled';
        } else if (partAssignments[i] != null) {
          groupIdx = partAssignments[i];
          partClass = 'visual-fraction-widget__part visual-fraction-widget__part--answer';
        }

        if (groupIdx !== null) {
          partClass += ` visual-fraction-widget__part--group-${groupIdx + 1} ${getGroupClassName(groupIdx)}`;
        }

        const colorKind = i < filledCount ? 'filled' : 'answer';
        const palette = colorKind === 'filled' ? filledPalette : answerPalette;
        const fallbackColor = palette[groupIdx ?? 0]!;

        const style: React.CSSProperties | undefined =
          groupIdx !== null
            ? {
                '--visual-fraction-part-color': `var(--visual-fraction-${colorKind}-group-${groupIdx + 1}-color, ${fallbackColor})`,
                '--visual-fraction-part-text-color': `var(--visual-fraction-${colorKind}-group-${groupIdx + 1}-text-color, ${getTextColorForBackground(fallbackColor)})`,
              } as React.CSSProperties
            : undefined;

        return {
          index: i,
          className: partClass,
          style,
          // A slot is interactive when there are answer groups AND it falls in the non-filled range.
          // answer.length counts groups (not total slots); the state hook handles per-slot cycling.
          isInteractiveSlot: answer.length > 0 && i >= filledCount,
          isSelected: groupIdx !== null,
          groupIndex: groupIdx,
        };
      }),
    [divisions, filledCount, filledPartGroups, partAssignments, answer, filledPalette, answerPalette],
  );

  const statusClass = validationStatus
    ? ` visual-fraction-widget--${validationStatus} widget--${validationStatus}`
    : '';

  const rootStyle = useMemo(() => getSizeStyle(widget.size, shape), [widget.size, shape]);

  const ShapeComponent = SHAPE_COMPONENTS[shape];

  return (
    <div
      className={`inline-widget visual-fraction-widget visual-fraction-widget--${shape}${statusClass}`}
      style={rootStyle}
      role="group"
      aria-label={`${divisions} part ${shape} fraction`}
    >
      <ShapeComponent
        parts={parts}
        divisions={divisions}
        orientation={orientation ?? 'horizontal'}
        onPartClick={handlePartClickWithSound}
      />

      {label && (
        <span className="visual-fraction-widget__label">{label}</span>
      )}
    </div>
  );
};

VisualFraction.displayName = 'VisualFraction';

/**
 * Map the unified author `size` (1 = small, 2 = bigger) onto the right CSS target per shape:
 *   bar / grid        → --visual-fraction-part-size  (one cell, rem)
 *   circle / triangle → --visual-fraction-size = size × VISUAL_FRACTION_SVG_SIZE_SCALE rem
 *
 * The scale factor keeps shapes visually comparable across the size unit:
 *   size=1.35 (bar default) → 1.35 × 8 = 10.8rem ≈ CSS clamp(7rem, 24vw, 12rem).
 * When `size` is omitted, CSS defaults apply automatically.
 */
function getSizeStyle(
  size: number | undefined,
  _shape: VisualFractionJSON['shape'],
): React.CSSProperties | undefined {
  if (size === undefined) return undefined;
  // All shapes: size scales the total container.
  // bar/grid use --visual-fraction-size as total span; --visual-fraction-part-size stays as bar thickness.
  return {
    '--visual-fraction-size': `${size * VISUAL_FRACTION_SVG_SIZE_SCALE}rem`,
  } as React.CSSProperties;
}
